import React, { PropsWithChildren, useState } from 'react'
import useEndpoint from '../../hooks/useEndpoint'
import {
  FetchNotificationsResponse,
  FetchNotificationsResponseNotifications,
  Notification,
  notificationApi,
  PaginationResponse,
} from '../../api/swagger'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../Routes/AuthProvider'
import { useLocation } from 'react-router-dom'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { FieldValidity } from '../Interfaces/FieldValidity'
import { useMountEffect } from '../../hooks/useMountEffect'

export interface NotificationFormValidity {
  name: FieldValidity
  body: FieldValidity
  startDate: FieldValidity
  startTime: FieldValidity
  endDate: FieldValidity
  endTime: FieldValidity
  regions: FieldValidity
  roles: FieldValidity
}

export const defaultNotificationContextValue = {
  notifications: [] as FetchNotificationsResponseNotifications[],
  userNotifications: [] as Notification[],
  pagination: undefined as PaginationResponse | undefined,
  updateNotificationsResponse: (response: FetchNotificationsResponse): void => {
    console.warn(
      `The NotificationContext.updateNotificationsResponse was called with ${response}. Did you forget to use a NotificationProvider?`
    )
  },

  resetContextToDefaults: (): void => {
    console.warn(
      `The NotificationContext.resetContextToDefaults was called default value. Did you forget to use a NotificationProvider?`
    )
  },

  resetEditContextValues: (): void => {
    console.warn(
      `The NotificationContext.resetEditContextValues was called default value. Did you forget to use a NotificationProvider?`
    )
  },

  /** Utilized for pulling notification details */
  selectedNotificationKey: undefined as number | undefined,
  updateSelectedNotificationKey: (newKey: number): void => {
    console.warn(
      `The NotificationContext.updateSelectedNotificationKey was called with ${newKey}. Did you forget to use a NotificationProvider?`
    )
  },

  notificationDetails: {
    name: '',
    body: '',
    startDate: new Date(),
    // set end date to 1 day in the future
    endDate: new Date(new Date().setUTCDate(new Date().getUTCDate() + 1)),
    regionKeys: [],
    roleKeys: [],
    color: '',
  } as Notification,
  updateNotificationDetails: (newDetails: Notification): void => {
    console.warn(
      `The NotificationContext.updateNotificationDetails was called with ${newDetails}. Did you forget to use a NotificationProvider?`
    )
  },

  formValidity: {
    name: { input: true },
    body: { input: true },
    startDate: { input: true, afterPrevious: true },
    startTime: { input: true, afterPrevious: true },
    endDate: { input: true, afterPrevious: true },
    endTime: { input: true, afterPrevious: true },
    regions: { input: true },
    roles: { input: true },
    // validity of role keys and region keys to come
  } as NotificationFormValidity,
  updateFormValidity: (validity: NotificationFormValidity): void => {
    console.warn(
      `The NotificationContext.updateFormValidity was called with ${validity}. Did you forget to use a NotificationProvider?`
    )
  },
}

export const NotificationContext = React.createContext(
  defaultNotificationContextValue
)

export const useNotificationContext =
  (): typeof defaultNotificationContextValue =>
    React.useContext(NotificationContext)

export type TestNotificationConfig = Partial<
  typeof defaultNotificationContextValue
>

export interface NotificationProviderProps extends PropsWithChildren {
  testConfig?: TestNotificationConfig
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  testConfig,
}) => {
  const { t } = useTranslation()
  const { permissionAbility, featureAbility } = useAuth()
  const location = useLocation()
  const {
    Notification: { fetchNotifications, fetchUserNotifications },
  } = useLoadingIds()
  const [notifications, setNotifications] = useState(
    defaultNotificationContextValue.notifications
  )
  const [userNotifications, setUserNotifications] = useState(
    defaultNotificationContextValue.userNotifications
  )
  const [pagination, setPagination] = useState(
    defaultNotificationContextValue.pagination
  )

  const [selectedNotificationKey, setSelectedNotificationKey] = useState(
    defaultNotificationContextValue.selectedNotificationKey
  )

  const [notificationDetails, setNotificationDetails] = useState(
    defaultNotificationContextValue.notificationDetails
  )

  const [formValidity, setFormValidity] = useState(
    defaultNotificationContextValue.formValidity
  )

  const updateFormValidity = setFormValidity

  const updateNotificationDetails = setNotificationDetails

  const updateSelectedNotificationKey = setSelectedNotificationKey

  const resetEditContextValues = () => {
    setSelectedNotificationKey(
      defaultNotificationContextValue.selectedNotificationKey
    )
    setNotificationDetails(defaultNotificationContextValue.notificationDetails)
    setFormValidity(defaultNotificationContextValue.formValidity)
  }

  const resetContextToDefaults = () => {
    setNotifications(defaultNotificationContextValue.notifications)
    setPagination(defaultNotificationContextValue.pagination)
    resetEditContextValues()
  }

  useEndpoint({
    loadingId: fetchNotifications,
    swaggerCall: async () => {
      const response = await notificationApi.fetchNotifications({})

      setNotifications(response.notifications)
      setPagination(response.pagination)
    },
    failureMessage: t(
      'Admin.NotificationContext.FetchNotifications.FailedToFetch',
      'Unable to retrieve notifications'
    ),
    dontCall: () => {
      return !(
        location.pathname !== '/admin/notifications' &&
        permissionAbility.can('view', 'Notification') &&
        featureAbility.can('notification', 'Feature')
      )
    },
  })

  const { triggerFetch: triggerFetchUserNotifications } = useEndpoint({
    loadingId: fetchUserNotifications,
    swaggerCall: async () => {
      const response = await notificationApi.fetchUserNotifications()

      setUserNotifications(response.notifications)
    },
    failureMessage: t(
      'Admin.NotificationContext.FetchUserNotifications.FailedToFetch',
      'Unable to retrieve user notifications.'
    ),
  })

  useMountEffect(() => {
    triggerFetchUserNotifications()
  })

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        userNotifications,
        pagination,
        updateNotificationsResponse: (response: FetchNotificationsResponse) => {
          setPagination(response.pagination)
          setNotifications(response.notifications)
        },
        selectedNotificationKey,
        updateSelectedNotificationKey,
        notificationDetails,
        updateNotificationDetails,
        formValidity,
        updateFormValidity,
        resetContextToDefaults,
        resetEditContextValues,
        ...testConfig,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}
